import React, { useState, useEffect, useRef, useImperativeHandle } from "react";
import classnames from "classnames";
import { Popup, Toast } from "zarm";
import { Icon } from "@iyunbao/components";
import LockScroll from '../../../lockScroll';
import { ISearchSource } from "../type";
import { getValue, validateLevel, disabledLevel, getSourceByValue } from "../util";

const initStepCrumbs = {
  label: "请继续选择",
  value: "-9999"
};

const StackStep = props => {
  const {
    prefixCls = "picker-step",
    className,
    labelPlaceholder,

    visible: initVisible,
    pickerPlaceholder,
    pickerTitle,
    pickerTips,

    value,
    options,
    valueMember = "value",
    max,
    min,

    dataSource,
    displayRender,
    validate,
    onOk,
    onCancel,
    onMaskClick,
    beforeRender,
    forwardedRef
  } = props;

  const initStepState = getValue(props);
  const stackStepRef = useRef<HTMLDivElement | null>(null);

  // 下拉抽屉打开或者关闭
  const [visible, setVisible] = useState(initVisible || false);

  // 是否已触发搜索
  const [isShowSearch, setIsShowSearch] = useState(false);

  // 搜索输入框输入的文案
  const [searchValue, setSearchValue] = useState("");

  // 被搜索的原数据
  const [searchSource, setSearchSource] = useState<ISearchSource[]>([]);

  // 搜索时过滤的数据列表
  const [searchResult, setSearchResult] = useState<ISearchSource[]>([]);

  // 分步骤选择时的数据源
  const [stepSource, setStepSource] = useState(dataSource || []);

  // 分步骤选择时的实时值
  const [stepValue, setStepValue] = useState(initStepState.defaultValue || []);

  // 页面展示的最终值
  const [displayLabel, setDisplayLabel] = useState(displayRender(initStepState.defaultValue));

  // 记录当前是第几步
  const [stepIndex, setStepIndex] = useState(initStepState.defaultValue.length ? initStepState.defaultValue.length - 1 : 0);

  useEffect(
    () => {
      // 格式化数据成方便查询和渲染的格式
      const searchSource: ISearchSource[] = [];
      const traversing = (data: ISearchSource[], lever: number, positionArr: any[], result: any[]) => {
        data.forEach((item, index) => {
          const nowPotionArr = [...positionArr];
          const nowResult = [...result];
          const nowItem = { ...item };

          nowPotionArr[lever] = index;
          delete nowItem.children;
          nowResult[lever] = nowItem;

          if (item.children && item.children.length) {
            traversing(item.children, lever + 1, nowPotionArr, nowResult);
          } else {
            searchSource.push({
              result: nowResult,
              label: item.label
            });
          }
        });
      };

      traversing(dataSource, 0, [], []);

      setSearchSource(searchSource);

      const asyncStepValue = getValue({ value, options, valueMember }, dataSource);
      const valueLength = asyncStepValue.defaultValue.length;

      if (valueLength) {
        setStepValue(asyncStepValue.defaultValue);
        setStepIndex(valueLength - 1);
        setDisplayLabel(displayRender(asyncStepValue.defaultValue));
        setStepSource(getSourceByValue(dataSource, asyncStepValue.defaultValue[valueLength - 1], list => list));
      } else {
        setStepSource(dataSource);
      }
    },
    [dataSource, value, options, valueMember, displayRender]
  );

  // 点击按钮 取消||关闭
  const handleCancel = () => {
    setVisible(false);
    setIsShowSearch(false);
    setSearchValue("");
    setSearchResult([]);
  };

  // 当插入常用职业时，给父组件调用的handleCancel 方法
  useImperativeHandle(forwardedRef, () => ({
    handleCancel: handleCancel
  }));

  // 点击蒙层关闭
  const handleMaskClick = () => {
    onCancel();
    handleCancel();
    if (typeof onMaskClick === "function") {
      onMaskClick();
    }
  };

  // 触发真实的搜索，过滤数据
  const triggerSearch = (filterValue: string) => {
    if (filterValue) {
      const searchTemp = searchSource.filter(item => {
        return item.label.indexOf(filterValue) !== -1;
      });
      setSearchResult(searchTemp || []);
    }
  };

  // 搜索输入框变化时触发
  const onSearchChange = (e: any) => {
    const filterValue = e.target.value;
    setSearchValue(filterValue);
    triggerSearch(filterValue);
  };

  const clearInput = () => {
    setSearchValue("");
    setSearchResult([]);
  };

  const searchOk = value => {
    const errorMsg = validate(value);

    if (!errorMsg) {
      onOk(value);
      handleCancel();
      setVisible(false);
    } else {
      Toast.show(errorMsg);
    }
  };

  const getHeight = () => {
    if (isShowSearch) {
      return { height: `calc(80vh - 152px)` };
    } else if (setStepValue.length) {
      return { height: `calc(80vh - 154px)` };
    } else {
      return { height: `calc(80vh - 96px)` };
    }
  };

  // 点击选择列表
  const handleStepSelect = item => {
    let newValue = stepValue;
    // 新增
    const isAdd = newValue.length === stepIndex;
    // 修改
    const isUpdate = newValue.length !== stepIndex;

    if (item.children && item.children.length) {
      // 当前项目是否已选中
      const isSelected = !!stepValue.find(stepItem => stepItem.value === item.value);

      if (isSelected) {
        setStepIndex(stepIndex + 1);
        setStepSource(item.children);
      } else {
        if (isAdd) {
          newValue[stepIndex] = item;
        }

        // 修改时丢掉当前选中后面的所有值
        if (isUpdate) {
          newValue = newValue.slice(0, stepIndex);
          newValue[stepIndex] = item;
        }

        // 没有请选择时添加一个
        if (newValue.find(itemS => itemS.value !== initStepCrumbs.value)) {
          newValue.push(initStepCrumbs);
        }
        // 不能投保的提示
        if (validate(newValue.filter(itemS => itemS.value !== initStepCrumbs.value))) {
          return;
        }

        setStepIndex(stepIndex + 1);
        setStepValue([...newValue]);
        setStepSource(item.children);
      }
    } else {
      newValue[stepIndex] = item;
      // 删掉请选择
      const okValue = newValue.filter(itemS => itemS.value !== initStepCrumbs.value);
      onOk(okValue);
      setStepValue(okValue);
      setVisible(false);
    }
  };

  // 已选中的每一项tab切换
  const handleCrumbsChange = (index: number) => {
    // 如果点的是请选择，找出子级
    if (stepValue.length === index + 1) {
      setStepSource(getSourceByValue(dataSource, stepValue[index - 1], (list, i) => list[i].children));
    } else {
      // 如果不是请选择，找出当前级
      setStepSource(getSourceByValue(dataSource, stepValue[index], list => list));
    }
    setStepIndex(index);
    setStepValue([...stepValue]);
  };

  const renderSearchItem = (data, idx) => {
    const textData = data.result.map((item, index) => {
      if (index < data.result.length - 1) {
        return item.label;
      }
      return data.label.replace(searchValue, `<span class="key-name">${searchValue}</span>`);
    });

    return (
      <div className={`${prefixCls}-search-result-item`} key={data.label + idx} onClick={() => searchOk(data.result)}>
        {textData.map((item, index) => {
          return (
            <span
              // eslint-disable-next-line react/no-array-index-key
              key={`${index}${idx}`}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: `${item}${index < textData.length - 1 ? "&nbsp;>&nbsp;" : ""}` }}
            />
          );
        })}
      </div>
    );
  };

  // 搜索栏渲染
  const renderSearchInput = () => {
    return (
      <div className={`${prefixCls}-search`}>
        <div className={`${prefixCls}-search-box`}>
          <Icon type="sousuo" />
          <input
            type="text"
            value={searchValue}
            placeholder={pickerPlaceholder}
            onChange={onSearchChange}
            onFocus={() => setIsShowSearch(true)}
          />
          <span>
            <Icon type="guanbi_" className="clean-input" style={{ opacity: searchValue ? 1 : 0 }} onClick={clearInput} />
          </span>
        </div>
        <div className={`${prefixCls}-search-btn`} onClick={() => triggerSearch(searchValue)}>
          搜索
        </div>
      </div>
    );
  };

  // 选中的值展示在头部
  const renderStepValueLabel = () => {
    if (isShowSearch) {
      if (searchResult.length) {
        return (
          <div className={`${prefixCls}-search-tips`}>
            共搜索到
            {searchResult.length}
            条结果
          </div>
        );
      }
      return null;
    }
    if (stepValue.length) {
      return (
        <div className={`${prefixCls}-crumbs`}>
          <div className={`${prefixCls}-crumbs-content`}>
            {stepValue.map((item, index) => {
              const clsCrumbs = classnames({
                [`${prefixCls}-crumbs-selected`]: true,
                "crumbs-active": index === stepIndex
              });
              return (
                <span key={item.value} className={clsCrumbs} onClick={() => handleCrumbsChange(index)}>
                  {item.label}
                </span>
              );
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  // 渲染是否可投的标签
  const renderTag = item => {
    if (disabledLevel({ item, min, max })) {
      return <span className={`${prefixCls}-stack-forbidden`}>不可投</span>;
    }
    return <span className={`${prefixCls}-stack-permission`}>可投</span>;
  };

  const renderContent = () => {
    return (
      <>
        <div className={`${prefixCls}-content`} style={{ ...getHeight(), display: isShowSearch ? "none" : "block" }}>
          {/* 常用职业 */}
          {(!stepValue || !stepValue.length) && beforeRender}
          {/* 选择职业 */}
          <div className={`${prefixCls}-stack`}>
            {pickerTips && <div className={`${prefixCls}-stack-title`}>{pickerTips}</div>}
            {stepSource.map(item => {
              const isLevel = item.level && max;
              const isSelected = Array.isArray(stepValue) && !!stepValue.find(stepItem => stepItem.value === item.value);

              const clsLabel = classnames({
                [`${prefixCls}-stack-active`]: isSelected,
                [`${prefixCls}-stack-text`]: true
              });

              return (
                <div
                  key={item.value}
                  className={`${prefixCls}-stack-item`}
                  onClick={() => {
                    if (validateLevel({ cols: item, min, max })) {
                      return;
                    }
                    handleStepSelect(item);
                  }}
                >
                  {isSelected && <span className={`${prefixCls}-stack-icon`} />}
                  <div className={`${prefixCls}-stack-label`}>
                    <span className={clsLabel}>{item.label}</span>
                    {isLevel && renderTag(item)}
                  </div>
                  {isLevel && <span className={`${prefixCls}-stack-level`}>{item.level}类</span>}
                </div>
              );
            })}
          </div>
        </div>
        <div className={`${prefixCls}-content`} style={{ ...getHeight(), display: isShowSearch ? "block" : "none" }}>
          {searchResult.map((item, index) => {
            return renderSearchItem(item, index);
          })}
        </div>
      </>
    );
  };

  const labelCls = classnames(`${prefixCls}-input`, className, {
    [`${prefixCls}-placeholder`]: !displayLabel
  });

  return (
    <div className={prefixCls} ref={stackStepRef}>
      <div className={labelCls} onClick={() => setVisible(true)}>
        {displayLabel || labelPlaceholder}
      </div>
      <LockScroll>
        <Popup visible={visible} onMaskClick={handleMaskClick} className="step-popup">
          <div className={`${prefixCls}-popup-wrapper`}>
            <div className={`${prefixCls}-wrapper`}>
              <div className={`${prefixCls}-header`}>
                <div
                  className={`${prefixCls}-header-back`}
                  style={{ opacity: isShowSearch ? 1 : 0 }}
                  onClick={() => setIsShowSearch(false)}
                >
                  <Icon type="zuo" />
                </div>
                <div className={`${prefixCls}-header-title`}>{pickerTitle}</div>
                <div className={`${prefixCls}-header-cancel`} onClick={() => handleCancel()}>
                  <Icon type="guanbi_" className={`${prefixCls}-header-close`} />
                </div>
              </div>
              {renderSearchInput()}
              {renderStepValueLabel()}
              {renderContent()}
            </div>
          </div>
        </Popup>
      </LockScroll>
    </div>
  );
};

export default React.memo(
  React.forwardRef((props, ref) => {
    return <StackStep {...props} forwardedRef={ref} />;
  })
);
